import React, { useState, useEffect } from "react";
import { Button, Tooltip, Text, Card } from '@nextui-org/react';
import { CopyToClipboard } from "react-copy-to-clipboard";
import { Document, Show, Hide } from "react-iconly";
import { useSelector } from "react-redux";

import { fetchWithJson } from "../lib/api";
import checkAccess from '../lib/checkAccess';
import { updateTitle } from "../lib/utils";
import { error, success } from "../lib/userAlerts";
import endpoint from "../config/endpoint.json";
import { mstore } from "../store";

const MASK_CHARACTER = '*';
const MASK_LENGTH = 76;
const SDN_MASK_LENGTH = 44;

const KeyRow = ({ label, value, isVisible, onCopy }) => (
  <div className="mb-4 transform transition-all duration-300 hover:scale-[1.01]">
    <div className="flex items-center justify-between bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors duration-200">
      <div className="flex-1">
        <Text className="text-sm font-medium text-gray-700 mb-1">{label}</Text>
        <div className="font-mono text-sm bg-white p-2 rounded border border-gray-200 overflow-hidden">
          {isVisible 
            ? value 
            : `${MASK_CHARACTER.repeat(MASK_LENGTH)}${value.substr(label === 'SDN API Key' ? SDN_MASK_LENGTH : MASK_LENGTH)}`
          }
        </div>
      </div>
      <Tooltip content={`Click to Copy ${label}`} rounded color="primary">
        <CopyToClipboard text={value} onCopy={onCopy}>
          <Button
            size="sm"
            ghost
            auto
            className="ml-4 mt-4 hover:bg-blue-50 transition-colors duration-200"
            icon={<Document size={20} />}
            onClick={() => success(`Copied ${label}`)}
          />
        </CopyToClipboard>
      </Tooltip>
    </div>
  </div>
);

const Developer = () => {
  const [state, setState] = useState({
    visible: false,
    apiKey: "",
    secretKey: "",
    sdnKey: "",
    keyRegenerate: false,
    keyAge: 0,
    keyStatus: "",
  });

  const clientId = useSelector(state => state.client?.id || mstore.getPath(["user", "company_id"]));
  const isCometControl = useSelector(state => state.isCometControl || false);

  useEffect(() => {
    updateTitle("Driver Details");
    const initializeData = async () => {
      await checkAccess(clientId);
      getApiKey();
    };
    initializeData();
  }, [clientId]);

  const makeAPICall = async (url) => {
    try {
      const response = await fetchWithJson(url, { 
        company_id: clientId, 
        is_comet_control: isCometControl 
      });
      const data = await response.json();
      
      setState(prev => ({
        ...prev,
        apiKey: data.api_key,
        secretKey: data.api_secret,
        keyAge: data.key_age,
        keyRegenerate: data.key_regenerate,
        keyStatus: data.key_status,
        sdnKey: data.sdn_key
      }));
    } catch (err) {
      error("Oops! error occurred. We are unable to fetch the API Key at this time.");
    }
  };

  const getApiKey = () => makeAPICall(endpoint.get_API_KEY);
  const regenerateApiKey = () => makeAPICall(endpoint.regenerate_API_KEY);

  const toggleVisibility = () => {
    setState(prev => ({ ...prev, visible: !prev.visible }));
  };

  const { visible, apiKey, secretKey, sdnKey, keyRegenerate, keyAge } = state;

  return (
    <div className="max-w-4xl p-6">
      <div className="transition-all duration-300 ease-in-out">
        <Card className="p-6 mb-6 shadow-lg hover:shadow-xl transition-shadow duration-300">
          <div className="flex items-center justify-between mb-4">
            <div>
              <Text className={`text-xl font-semibold transition-colors duration-300 ${keyRegenerate ? 'text-red-600' : 'text-gray-800'}`}>
                API Key Status
              </Text>
              <Text className="text-gray-600 mt-1">
                It's been <span className={keyAge > 90 ? 'text-red-600 font-semibold' : ''}>{keyAge} Day(s) </span> since the API key was last generated.
              </Text>
            </div>
            <div className="flex items-center space-x-2">
              <Button
                auto
                color="primary"
                onClick={regenerateApiKey}
                className="hover:bg-blue-600 transition-colors duration-200"
              >
                Generate New Key
              </Button>
              <Button
                ghost
                color="secondary"
                icon={visible ? <Hide size={20} /> : <Show size={20} />}
                onClick={toggleVisibility}
                className="hover:bg-gray-100 transition-colors duration-200"
              >
                {visible ? 'Hide Keys' : 'Show Keys'}
              </Button>
            </div>
          </div>

          <div className="bg-blue-50 rounded-lg p-4 mb-6 transition-colors duration-300">
            <Text className="text-blue-800">
              <b>My Truckin' Tech</b> recommends generating a new API key <b>once every 90 days</b>.
            </Text>
          </div>
          <div className="space-y-4">
            <KeyRow 
              label="Customer Key"
              value={apiKey}
              isVisible={visible}
              onCopy={() => {}}
            />
            <KeyRow 
              label="Customer Secret"
              value={secretKey}
              isVisible={visible}
              onCopy={() => {}}
            />
            <KeyRow 
              label="SDN API Key"
              value={sdnKey}
              isVisible={visible}
              onCopy={() => {}}
            />
          </div>
        </Card>
      </div>
    </div>
  );
};

export default Developer;